// The single source of truth for *what users can do*.
//
// Every capability has:
//   id           — dot-namespaced string, used by routes + UI feature flags
//   description  — one line, shown in admin tooling
//   scope        — 'global' | 'org' | 'project' | 'section'
//   check        — (user, ctx) → { ok: boolean, reason?: string, scope?: string }
//
// Guard middleware (see middleware.js) and the React `<Capable>` component
// (frontend) both consult this table. Adding a new feature means adding a
// capability here and binding routes/UI to it — not writing yet another
// ad-hoc permission function scattered across services.

import {
  PLATFORM_ROLES, PROJECT_ROLES,
  isPlatformAdmin, isSuperAdmin, isPlatformStaff, isPayingRole
} from './roles.js';
import {
  LEVELS, EXPERT_ENTRY, MARKETPLACE_ELIGIBLE, WHOLE_REPORT_REVIEW,
  RULE_AUTHOR, TRACK_COUNCIL_MEMBER, TRACK_OWNER, CROSS_TRACK,
  GOVERNANCE_ENTRY, CONSTITUTIONAL, SUPREME, meetsLevel
} from './levels.js';
import { TRACKS, levelInTrack, maxLevel, bestLevelAcross } from './tracks.js';

// ────────────────────────────────────────────────────────────────────────
// Building blocks
// ────────────────────────────────────────────────────────────────────────

const ok = (scope = 'global') => ({ ok: true, scope });
const deny = (reason) => ({ ok: false, reason });

const hasUserId = (user) => Boolean(user?.userId);

// ── Commercial tier ─────────────────────────────────────────────────
//
// `isPayingRole(user.role)` is the wrong question, and every capability
// below used to ask it. It tests for the platform role `paid_member`,
// which NOTHING in the codebase ever assigns — a subscriber's role stays
// `member` and their commercial state lives on `subscription.tier`
// (free | paid_individual | paid_org), stamped into the token by
// TokenService and forwarded on `x-user`. So every paid-tier capability
// was false for every real paying customer.
//
// This is the same defect `report/viewerTier.js` fixed on the report path;
// the capability table was left behind because nothing enforces it yet,
// which makes it a trap rather than a live bug: the first route to mount
// `requireCapability` would have denied paying customers.
//
// The role check stays as a fallback so staff/admin — who have no
// subscription but must be able to exercise these paths — still pass.
const PAID_TIERS = new Set(['paid_individual', 'paid_org']);

const isPayingTier = (user) => {
  const tier = String(user?.subscription?.tier || user?.tier || '').toLowerCase();
  if (PAID_TIERS.has(tier)) return true;
  return isPayingRole(user?.role);
};

// Can this account hold a MOX balance at all? Earning does not require a
// subscription — a Member with an expert grant earns from reviews — so
// wallet access is broader than the paid tier.
const hasWallet = (user) => {
  if (!hasUserId(user)) return false;
  if (user.role === PLATFORM_ROLES.GUEST) return false;
  return true;
};

// Project-ownership check. The owning user is identified either by
// `ctx.projectOwnerId` (preferred) or by the legacy `req.body.ownerId`.
const isProjectOwner = (user, ctx) => {
  if (!user?.userId) return false;
  const ownerId = ctx?.projectOwnerId ?? ctx?.ownerId;
  return ownerId && user.userId === ownerId;
};

// Project-collaborator check (any of: Owner / InvitedUser / InvitedExpert
// with an active scope). The service that owns project ACLs is expected
// to enrich ctx with the user's project_role on this project before
// calling can(); during the M-001 transition, we accept either an
// explicit project_role on the user or an explicit projectOwnerId match.
const projectRole = (user, ctx) => {
  if (!user?.userId) return null;
  if (isProjectOwner(user, ctx)) return PROJECT_ROLES.OWNER;
  return ctx?.projectRole || user?.projectRoles?.[ctx?.projectId] || null;
};

// Two-person rule for super-admin sensitive operations: the request
// must carry a second super-admin's signed approval token in the past
// 5 minutes. Enforcement is performed by middleware; the capability
// table simply requires `ctx.twoPersonRule.verified === true`.
const twoPersonRuleVerified = (ctx) =>
  Boolean(ctx?.twoPersonRule?.verified) &&
  Number(ctx.twoPersonRule.verifiedAt) > Date.now() - 5 * 60 * 1000;

// ────────────────────────────────────────────────────────────────────────
// The capability table itself
// ────────────────────────────────────────────────────────────────────────

export const CAPABILITIES = Object.freeze({

  // ── Projects ──────────────────────────────────────────────────────
  'project.create': {
    description: 'Start a new project',
    scope: 'global',
    check: (user) => {
      if (!user) return deny('UNAUTHENTICATED');
      // Even guests can create one project. The guest-quota policy is
      // enforced at service layer (1 active project per guest).
      return ok();
    }
  },

  'project.read': {
    description: 'Read a project (any user with explicit access)',
    scope: 'project',
    check: (user, ctx) => {
      if (!user) return deny('UNAUTHENTICATED');
      if (isPlatformAdmin(user.role)) return ok('platform_admin');
      const r = projectRole(user, ctx);
      if (r === PROJECT_ROLES.OWNER) return ok('project_owner');
      if (r === PROJECT_ROLES.INVITED_USER) return ok('invited_user');
      if (r === PROJECT_ROLES.INVITED_VIEWER) return ok('invited_viewer');
      if (r === PROJECT_ROLES.INVITED_EXPERT) return ok('invited_expert');
      // Public visibility is enforced at the service layer (project.visibility).
      if (ctx?.projectVisibility === 'public') return ok('public');
      if (ctx?.projectVisibility === 'unlisted' && ctx?.hasShareToken) return ok('share_link');
      return deny('NOT_A_PARTICIPANT');
    }
  },

  'project.update': {
    description: 'Edit a project (answers, settings, chat)',
    scope: 'project',
    check: (user, ctx) => {
      if (!user) return deny('UNAUTHENTICATED');
      if (isPlatformAdmin(user.role)) return ok('platform_admin');
      const r = projectRole(user, ctx);
      if (r === PROJECT_ROLES.OWNER) return ok('project_owner');
      if (r === PROJECT_ROLES.INVITED_USER) return ok('invited_user');
      return deny('NOT_AN_EDITOR');
    }
  },

  'project.delete': {
    description: 'Delete a project (owner only, with grace period)',
    scope: 'project',
    check: (user, ctx) => {
      if (!user) return deny('UNAUTHENTICATED');
      if (isProjectOwner(user, ctx)) return ok('project_owner');
      if (isPlatformAdmin(user.role)) return ok('platform_admin');
      return deny('OWNER_ONLY');
    }
  },

  'project.transfer_ownership': {
    description: 'Transfer ownership to another account',
    scope: 'project',
    check: (user, ctx) =>
      isProjectOwner(user, ctx) ? ok('project_owner') :
      isPlatformAdmin(user?.role) ? ok('platform_admin') :
      deny('OWNER_ONLY')
  },

  'project.set_visibility': {
    description: 'Change private/org/unlisted/public visibility',
    scope: 'project',
    check: (user, ctx) =>
      isProjectOwner(user, ctx) ? ok('project_owner') :
      isPlatformAdmin(user?.role) ? ok('platform_admin') :
      deny('OWNER_ONLY')
  },

  'project.invite.viewer': {
    description: 'Issue a read-only share link',
    scope: 'project',
    check: (user, ctx) =>
      isProjectOwner(user, ctx) ? ok('project_owner') :
      projectRole(user, ctx) === PROJECT_ROLES.INVITED_USER ? ok('invited_user') :
      deny('NOT_AN_EDITOR')
  },

  'project.invite.collaborator': {
    description: 'Invite a collaborator with edit rights',
    scope: 'project',
    check: (user, ctx) =>
      isProjectOwner(user, ctx) ? ok('project_owner') :
      deny('OWNER_ONLY')
  },

  'project.invite.expert': {
    description: 'Invite an expert reviewer (time-boxed, scope-limited)',
    scope: 'project',
    check: (user, ctx) =>
      isProjectOwner(user, ctx) ? ok('project_owner') :
      deny('OWNER_ONLY')
  },

  'project.finalise': {
    description: 'Lock the project — finalises the report',
    scope: 'project',
    check: (user, ctx) =>
      isProjectOwner(user, ctx) ? ok('project_owner') :
      isPlatformAdmin(user?.role) ? ok('platform_admin') :
      deny('OWNER_ONLY')
  },

  // ── Answers (within a project) ────────────────────────────────────
  'answer.edit': {
    description: 'Override the value of an answer on a project',
    scope: 'project',
    check: (user, ctx) => {
      if (!user?.userId) return deny('UNAUTHENTICATED');
      if (isPlatformAdmin(user.role)) return ok('platform_admin');
      if (isProjectOwner(user, ctx)) return ok('project_owner');
      // Domain experts at L8+ may edit answers in projects they have an
      // active review scope on, within their granted sections.
      if (
        projectRole(user, ctx) === PROJECT_ROLES.INVITED_EXPERT &&
        bestLevelAcross(user, ctx?.expertTracks || []) >= RULE_AUTHOR
      ) return ok('invited_expert');
      return deny('FORBIDDEN_PROJECT_ANSWER');
    }
  },

  'answer.review': {
    description: 'Approve / correct / flag an answer (expert review verdict)',
    scope: 'project',
    check: (user, ctx) => {
      if (!user?.userId) return deny('UNAUTHENTICATED');
      // Project owner can ratify their own derivations (same as today).
      if (isProjectOwner(user, ctx)) return ok('project_owner');
      // Or an Invited Expert acting within their granted scope.
      if (projectRole(user, ctx) === PROJECT_ROLES.INVITED_EXPERT) {
        const lvl = bestLevelAcross(user, ctx?.expertTracks || []);
        if (lvl >= MARKETPLACE_ELIGIBLE) return ok('invited_expert');
      }
      // Or an admin doing platform-level review.
      if (isPlatformAdmin(user.role)) return ok('platform_admin');
      return deny('FORBIDDEN_EXPERT_REVIEW');
    }
  },

  // ── Reports ───────────────────────────────────────────────────────
  'report.preview': {
    description: 'Preview the rendered solution report in-app',
    scope: 'project',
    check: (user, ctx) => {
      // Anyone with project.read may preview; teaser vs full is decided
      // by tier at the rendering layer.
      const r = projectRole(user, ctx);
      if (r) return ok(`as_${r}`);
      if (isPlatformAdmin(user?.role)) return ok('platform_admin');
      if (ctx?.projectVisibility === 'public') return ok('public');
      if (ctx?.projectVisibility === 'unlisted' && ctx?.hasShareToken) return ok('share_link');
      return deny('NOT_A_PARTICIPANT');
    }
  },

  'report.export.pdf': {
    description: 'Download report as PDF (free for Members; watermarked for Guests)',
    scope: 'project',
    check: (user, ctx) => {
      if (!user) return deny('UNAUTHENTICATED');
      const r = projectRole(user, ctx);
      if (!r && !isPlatformAdmin(user.role)) return deny('NOT_A_PARTICIPANT');
      return ok();
    }
  },

  'report.export.docx': {
    description: 'Download as DOCX (paid tier)',
    scope: 'project',
    check: (user, ctx) => {
      if (!isPayingTier(user)) return deny('PAID_TIER_REQUIRED');
      const r = projectRole(user, ctx);
      if (!r && !isPlatformAdmin(user.role)) return deny('NOT_A_PARTICIPANT');
      return ok();
    }
  },

  'report.export.markdown': {
    description: 'Download as Markdown (any Member)',
    scope: 'project',
    check: (user, ctx) => {
      if (!user) return deny('UNAUTHENTICATED');
      if (user.role === PLATFORM_ROLES.GUEST) return deny('MEMBER_REQUIRED');
      const r = projectRole(user, ctx);
      if (!r && !isPlatformAdmin(user.role)) return deny('NOT_A_PARTICIPANT');
      return ok();
    }
  },

  'report.share.public': {
    description: 'Generate a read-only share link',
    scope: 'project',
    check: (user, ctx) =>
      isProjectOwner(user, ctx) ? ok('project_owner') : deny('OWNER_ONLY')
  },

  'report.share.password': {
    description: 'Generate a password-protected share link (paid)',
    scope: 'project',
    check: (user, ctx) => {
      if (!isProjectOwner(user, ctx)) return deny('OWNER_ONLY');
      if (!isPayingTier(user)) return deny('PAID_TIER_REQUIRED');
      return ok('project_owner_paid');
    }
  },

  'report.share.brand_whitelabel': {
    description: 'Remove Moksy branding from exported / shared reports',
    scope: 'project',
    check: (user, ctx) => {
      if (!isProjectOwner(user, ctx)) return deny('OWNER_ONLY');
      const isAgency = user.role === PLATFORM_ROLES.AGENCY;
      return isAgency ? ok('agency_tier') : deny('AGENCY_TIER_REQUIRED');
    }
  },

  'report.email.self': {
    description: 'Email the report to yourself',
    scope: 'project',
    check: (user, ctx) => {
      const r = projectRole(user, ctx);
      if (r) return ok(`as_${r}`);
      return deny('NOT_A_PARTICIPANT');
    }
  },

  'report.email.team': {
    description: 'Email the report to multiple recipients (paid)',
    scope: 'project',
    check: (user, ctx) => {
      if (!isPayingTier(user)) return deny('PAID_TIER_REQUIRED');
      if (!isProjectOwner(user, ctx)) return deny('OWNER_ONLY');
      return ok();
    }
  },

  // ── Report Manager (operator/admin tool) ──────────────────────────────
  // The cross-collection data console (query / export / import). These are
  // GLOBAL-scope platform capabilities — distinct from the project-scoped
  // `report.*` (solution report) capabilities above. Enforced by the
  // reporting engine hosted in the downloads service.
  'reports.data.read': {
    description: 'Query and preview registered platform data collections',
    scope: 'global',
    check: (user) => (isPlatformStaff(user?.role) || isPlatformAdmin(user?.role))
      ? ok('platform_staff') : deny('STAFF_ONLY')
  },

  'reports.data.export': {
    description: 'Export registered data collections to file (async job)',
    scope: 'global',
    check: (user) => isPlatformAdmin(user?.role) ? ok('platform_admin') : deny('ADMIN_ONLY')
  },

  'reports.data.import': {
    description: 'Import data into import-enabled collections (two-phase, reversible)',
    scope: 'global',
    check: (user) => isSuperAdmin(user?.role) ? ok('super_admin') : deny('SUPER_ADMIN_ONLY')
  },

  'reports.finance.read': {
    description: 'Query and preview finance/billing collections (payments)',
    scope: 'global',
    check: (user) => isSuperAdmin(user?.role) ? ok('super_admin') : deny('SUPER_ADMIN_ONLY')
  },

  'reports.finance.export': {
    description: 'Export finance/billing collections to file',
    scope: 'global',
    check: (user) => isSuperAdmin(user?.role) ? ok('super_admin') : deny('SUPER_ADMIN_ONLY')
  },

  // ── Expert marketplace ────────────────────────────────────────────
  'review.request': {
    description: 'Request an expert review on a finalised project',
    scope: 'project',
    check: (user, ctx) => {
      // Project-ownership only at this layer. The actual mox spend is
      // enforced by holdEscrow() at the service layer — if the user
      // doesn't have enough mox, they get a clean MOX_INSUFFICIENT_BALANCE.
      // This lets guests (with a starter grant) try the marketplace
      // before upgrading; paying tiers just have more mox to spend.
      if (!isProjectOwner(user, ctx)) return deny('OWNER_ONLY');
      return ok();
    }
  },

  'review.accept_assignment': {
    description: 'Accept an offered review job',
    scope: 'global',
    check: (user, ctx) => {
      const lvl = bestLevelAcross(user, ctx?.tracks || []);
      if (lvl < MARKETPLACE_ELIGIBLE) return deny('LEVEL_TOO_LOW');
      return ok();
    }
  },

  'review.submit_section': {
    description: 'Submit a section review (paid eligibility)',
    scope: 'project',
    check: (user, ctx) => {
      if (projectRole(user, ctx) !== PROJECT_ROLES.INVITED_EXPERT) return deny('NOT_INVITED_EXPERT');
      const lvl = bestLevelAcross(user, ctx?.expertTracks || []);
      if (lvl < MARKETPLACE_ELIGIBLE) return deny('LEVEL_TOO_LOW');
      return ok();
    }
  },

  'review.submit_whole_report': {
    description: 'Submit a whole-report review (L7+ in any granted track)',
    scope: 'project',
    check: (user, ctx) => {
      if (projectRole(user, ctx) !== PROJECT_ROLES.INVITED_EXPERT) return deny('NOT_INVITED_EXPERT');
      const lvl = bestLevelAcross(user, ctx?.expertTracks || []);
      if (lvl < WHOLE_REPORT_REVIEW) return deny('LEVEL_TOO_LOW');
      return ok();
    }
  },

  'review.adjudicate': {
    description: 'Adjudicate a contested review (L8+ practitioner-architect)',
    scope: 'global',
    check: (user, ctx) => {
      const lvl = bestLevelAcross(user, ctx?.tracks || []);
      if (lvl < RULE_AUTHOR) return deny('LEVEL_TOO_LOW');
      return ok();
    }
  },

  // ── Catalog co-authorship ─────────────────────────────────────────
  'catalog.read.public': {
    description: 'Read the public-facing catalogue',
    scope: 'global',
    check: () => ok() // Public knowledge by design (CC-attribution)
  },

  'catalog.question.propose': {
    description: 'Propose a new catalogue question (L7+ in the track, or platform staff)',
    scope: 'global',
    check: (user, ctx) => {
      const track = ctx?.track;
      if (!track) return deny('TRACK_REQUIRED');
      // 🏛️ Platform staff, like every other capability in this file that has
      // a staff path — and unlike this one, which had none.
      //
      // The omission made two gates mutually exclusive in practice. The only
      // route to this capability is `POST /admin/learning/proposal-clusters/draft`,
      // which is admin-gated; and then the track rule asked that same admin
      // for L7 in the category's reviewer track, which platform staff do not
      // hold. So the learning loop's last step refused everyone who could
      // reach it, with LEVEL_TOO_LOW, and the corpus of corrections had never
      // produced a single proposal.
      //
      // This grants nothing a proposal does not already imply: proposing is
      // not changing. Every proposal is PENDING and still goes through the
      // permissioned category→master→base approval, which has its own — and
      // deliberately separate — authority check.
      if (isPlatformAdmin(user?.role)) return ok('platform_admin');
      if (levelInTrack(user, track) < WHOLE_REPORT_REVIEW) return deny('LEVEL_TOO_LOW');
      return ok();
    }
  },

  'catalog.rule.propose': {
    description: 'Propose a new derivation rule (L8+ in the track)',
    scope: 'global',
    check: (user, ctx) => {
      const track = ctx?.track;
      if (!track) return deny('TRACK_REQUIRED');
      if (levelInTrack(user, track) < RULE_AUTHOR) return deny('LEVEL_TOO_LOW');
      return ok();
    }
  },

  // ── Template proposal pipeline (category -> master -> base) ────────
  // A proposed question/value change is approved at three progressively
  // senior gates before it's written back onto the live Base data
  // (see TemplateProposalService). Category tier: the track's one
  // appointed owner. Master tier: cross-track Guru authority. Base
  // tier: reuses the existing 'catalog.question.publish' (admin-only).
  'catalog.question.approveCategory': {
    description: 'Approve a template proposal at category tier (track owner, L10+ in the track)',
    scope: 'global',
    check: (user, ctx) => {
      if (isPlatformAdmin(user?.role)) return ok('platform_admin');
      const track = ctx?.track;
      if (!track) return deny('TRACK_REQUIRED');
      if (levelInTrack(user, track) < TRACK_OWNER) return deny('LEVEL_TOO_LOW');
      return ok('track_owner');
    }
  },

  'catalog.question.approveMaster': {
    description: 'Approve a template proposal at master tier (Guru, L11+ cross-track)',
    scope: 'global',
    check: (user, ctx) => {
      if (isPlatformAdmin(user?.role)) return ok('platform_admin');
      if (maxLevel(user) < CROSS_TRACK) return deny('LEVEL_TOO_LOW');
      return ok('guru');
    }
  },

  'catalog.question.publish': {
    description: 'Publish a proposed question to the live catalogue (admin)',
    scope: 'global',
    check: (user) => isPlatformAdmin(user?.role) ? ok('platform_admin') : deny('ADMIN_ONLY')
  },

  'catalog.rule.publish': {
    description: 'Publish a proposed rule to the live catalogue (admin)',
    scope: 'global',
    check: (user) => isPlatformAdmin(user?.role) ? ok('platform_admin') : deny('ADMIN_ONLY')
  },

  'catalog.rule.rollback': {
    description: 'Roll back a published rule to a previous version',
    scope: 'global',
    check: (user) => isPlatformAdmin(user?.role) ? ok('platform_admin') : deny('ADMIN_ONLY')
  },

  'catalog.version.publish': {
    description: 'Cut a new catalogue version (admin; L13+ if track-spanning)',
    scope: 'global',
    check: (user, ctx) => {
      if (!isPlatformAdmin(user?.role)) return deny('ADMIN_ONLY');
      if (ctx?.crossTrack && (user.governanceLevel || 0) < LEVELS.ARCHITECT) {
        return deny('CROSS_TRACK_REQUIRES_L13');
      }
      return ok();
    }
  },

  // ── Wallet & mox ──────────────────────────────────────────────────
  'wallet.read.own': {
    description: 'Read your own wallet balance + ledger',
    scope: 'global',
    check: (user) => hasUserId(user) ? ok() : deny('UNAUTHENTICATED')
  },

  'wallet.withdraw': {
    description: 'Initiate a fiat withdrawal (requires KYC)',
    scope: 'global',
    check: (user, ctx) => {
      if (!hasUserId(user)) return deny('UNAUTHENTICATED');
      // Anyone who can hold a balance can withdraw it, subject to KYC.
      // Earning does not require a paid plan, so neither can cashing out.
      if (!hasWallet(user)) return deny('NO_WALLET');

      // KYC comes from the CALLER, not from the user object.
      //
      // This used to read `user.kycVerified` — a field no service has ever
      // written. It is not a JWT claim and not on the User model, so the
      // check was `!undefined` for everybody and the endpoint returned
      // KYC_REQUIRED to every caller including admins. Withdrawal was not
      // gated; it was closed.
      //
      // The authority is users-service (`getVerificationState`, the RV4
      // stage), which the caller queries and passes in. Keeping it in ctx
      // rather than on the token matters: identity verification can be
      // revoked, and a token minted before that happened must not still
      // assert it.
      if (ctx?.kycVerified !== true) return deny('KYC_REQUIRED');
      return ok();
    }
  },

  'wallet.spend.review_request': {
    description: 'Spend mox / fiat on a review request',
    scope: 'global',
    check: (user) => isPayingTier(user) ? ok() : deny('PAID_TIER_REQUIRED')
  },

  // ── Platform admin ────────────────────────────────────────────────
  'admin.user.impersonate': {
    description: 'View another user with just-in-time consent token (audited)',
    scope: 'global',
    check: (user, ctx) => {
      if (!isPlatformStaff(user?.role)) return deny('STAFF_ONLY');
      if (!ctx?.consentToken?.verified) return deny('CONSENT_REQUIRED');
      return ok('staff_with_consent');
    }
  },

  'admin.user.suspend': {
    description: 'Suspend a user account',
    scope: 'global',
    check: (user) => isPlatformAdmin(user?.role) ? ok() : deny('ADMIN_ONLY')
  },

  'admin.expert.promote': {
    description: 'Promote/demote an expert level (admin tier)',
    scope: 'global',
    check: (user, ctx) => {
      if (!isPlatformAdmin(user?.role)) return deny('ADMIN_ONLY');
      // Cross-Guru promotion needs Super Admin.
      if (ctx?.targetLevel >= CROSS_TRACK && !isSuperAdmin(user.role)) {
        return deny('SUPER_ADMIN_ONLY_FOR_GURU');
      }
      return ok();
    }
  },

  'admin.feature_flag.toggle': {
    description: 'Flip a feature flag',
    scope: 'global',
    check: (user) => isPlatformAdmin(user?.role) ? ok() : deny('ADMIN_ONLY')
  },

  'admin.mox_rate.update': {
    description: 'Change the mox-to-fiat exchange rate (Super Admin + two-person)',
    scope: 'global',
    check: (user, ctx) => {
      if (!isSuperAdmin(user?.role)) return deny('SUPER_ADMIN_ONLY');
      if (!twoPersonRuleVerified(ctx)) return deny('TWO_PERSON_RULE_REQUIRED');
      return ok();
    }
  },

  'admin.role_definitions.update': {
    description: 'Change role / capability definitions (Super Admin + two-person)',
    scope: 'global',
    check: (user, ctx) => {
      if (!isSuperAdmin(user?.role)) return deny('SUPER_ADMIN_ONLY');
      if (!twoPersonRuleVerified(ctx)) return deny('TWO_PERSON_RULE_REQUIRED');
      return ok();
    }
  },

  'admin.audit_log.read': {
    description: 'Read the platform audit log',
    scope: 'global',
    check: (user) => isPlatformStaff(user?.role) ? ok() : deny('STAFF_ONLY')
  },

  'assessment.taxonomy.read': {
    description: 'View the project maturity & risk assessment taxonomy (pillars, sub-pillars, dimensions, rules)',
    scope: 'global',
    check: (user) => isPlatformStaff(user?.role) ? ok() : deny('STAFF_ONLY')
  },

  'assessment.taxonomy.write': {
    description: 'Create, edit, or archive rows in the assessment taxonomy',
    scope: 'global',
    check: (user) => isPlatformAdmin(user?.role) ? ok() : deny('ADMIN_ONLY')
  },

  // RF-105: "reviewing someone else's export is a senior duty, and the
  // requester cannot approve their own" — the masking mechanism itself was
  // real and correct (packages/reporting's redact.js); nothing stopped a
  // super admin unmasking their OWN request. Same two-person shape as the
  // three capabilities above, applied to the one real lever that turns PII
  // masking off.
  'admin.pii_unmask': {
    description: 'Export or preview a reporting resource with PII unmasked (Super Admin + two-person)',
    scope: 'global',
    check: (user, ctx) => {
      if (!isSuperAdmin(user?.role)) return deny('SUPER_ADMIN_ONLY');
      if (!twoPersonRuleVerified(ctx)) return deny('TWO_PERSON_RULE_REQUIRED');
      return ok();
    }
  },

  'admin.kill_switch': {
    description: 'Trigger a platform kill-switch (Super Admin + two-person)',
    scope: 'global',
    check: (user, ctx) => {
      if (!isSuperAdmin(user?.role)) return deny('SUPER_ADMIN_ONLY');
      if (!twoPersonRuleVerified(ctx)) return deny('TWO_PERSON_RULE_REQUIRED');
      return ok();
    }
  }

});

// Convenience: list of all capability IDs (used by tests + the
// frontend `<Capable>` component to validate at compile-time).
export const CAPABILITY_IDS = Object.freeze(Object.keys(CAPABILITIES));

/**
 * The capabilities that require a second person, DERIVED rather than listed.
 *
 * Enforcement lives outside this file — a middleware has to capture the second
 * approval and put it on `ctx` before the check runs — so the enforcer needs
 * to know which capabilities carry the rule. Writing that as a second literal
 * list is how the two drift: someone adds `twoPersonRuleVerified` to a new
 * capability, the list is not updated, and the rule silently applies to
 * nothing. That failure has already happened once here at a larger scale —
 * the rule was declared on three capabilities and mounted on no route at all.
 *
 * So the set is computed by asking each capability the question directly:
 * given a super-admin with no verified approval, does it refuse with
 * TWO_PERSON_RULE_REQUIRED? Fifty-one calls at module load, and it cannot
 * disagree with the checks because it IS the checks.
 */
export const TWO_PERSON_CAPABILITIES = Object.freeze(
  CAPABILITY_IDS.filter((id) => {
    try {
      const verdict = CAPABILITIES[id].check({ role: 'super_admin', userId: '__probe__' }, {});
      return verdict?.ok === false && verdict.reason === 'TWO_PERSON_RULE_REQUIRED';
    } catch {
      // A check that throws on a bare context tells us nothing about the
      // two-person rule; treat it as not requiring one rather than guessing.
      return false;
    }
  })
);
